import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ServiceAppointmentService } from '../../../core/services/service-appointment.service';
import { OrganizationService } from '../../../core/services/organization.service';
import { WebSocketService } from '../../../core/services/websocket.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  ServiceAppointment,
  ServiceAppointmentStatus,
  ServiceAppointmentStatusCounts,
  SERVICE_APPOINTMENT_STATUSES,
  PREFERRED_TIME_OPTIONS
} from '../../../core/models/service-appointment.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

interface FilterState {
  status: string;
}

@Component({
  selector: 'app-service-appointments',
  standalone: true,
  imports: [CommonModule, FormsModule, MaxLengthDirective, CharCounterDirective],
  templateUrl: './service-appointments.component.html',
  styleUrls: ['./service-appointments.component.scss']
})
export class ServiceAppointmentsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  Math = Math;

  isLoading = false;
  appointments: ServiceAppointment[] = [];

  // Organization context
  organizationId: string | null = null;

  // Status counts
  statusCounts: ServiceAppointmentStatusCounts = {
    pending: 0,
    requested: 0,  // deprecated
    scheduled: 0,
    confirmed: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0
  };

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalAppointments = 0;
  totalPages = 0;

  // Filter state
  filters: FilterState = {
    status: ''
  };
  activeFilterCount = 0;

  // Status options for filter dropdown
  statusOptions = SERVICE_APPOINTMENT_STATUSES;

  // Cancel confirmation
  appointmentToCancel: ServiceAppointment | null = null;
  cancelReason = '';
  isCancelling = false;
  showCancelModal = false;

  constructor(
    private router: Router,
    private appointmentService: ServiceAppointmentService,
    private organizationService: OrganizationService,
    private websocketService: WebSocketService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.initialize();
    this.subscribeToWebSocketEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async initialize(): Promise<void> {
    this.isLoading = true;
    try {
      const org = await this.organizationService.getMyOrganization();
      if (!org) {
        console.error('No organization found');
        this.isLoading = false;
        return;
      }
      this.organizationId = org.id;

      await Promise.all([
        this.loadAppointments(),
        this.loadStatusCounts()
      ]);
    } catch (error) {
      console.error('Error initializing service appointments:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private subscribeToWebSocketEvents(): void {
    this.websocketService.on<{
      appointmentId: string;
      status: string;
      droneName: string;
      timestamp: string;
    }>('appointment.created').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      this.refreshList();
      this.toastService.info(`New appointment requested for ${event.droneName || 'drone'}`, {
        title: 'Service Appointment',
        duration: 6000,
        icon: 'bi-calendar-check',
        action: {
          label: 'View',
          callback: () => this.router.navigate(['/maintenance/service-appointments', event.appointmentId])
        }
      });
    });

    this.websocketService.on<{
      appointmentId: string;
      status: string;
      timestamp: string;
    }>('appointment.updated').pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.refreshList();
    });

    this.websocketService.on<{
      appointmentId: string;
      oldStatus: string;
      newStatus: string;
      droneName: string;
      timestamp: string;
    }>('appointment.statusChanged').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      this.refreshList();
      const statusText = this.getStatusText(event.newStatus);
      const droneInfo = event.droneName ? ` for ${event.droneName}` : '';
      this.toastService.info(`Appointment${droneInfo} is now ${statusText}`, {
        title: 'Status Update',
        duration: 5000,
        icon: 'bi-calendar-check',
        action: {
          label: 'View',
          callback: () => this.router.navigate(['/maintenance/service-appointments', event.appointmentId])
        }
      });
    });
  }

  private async refreshList(): Promise<void> {
    await Promise.all([
      this.loadAppointments(),
      this.loadStatusCounts()
    ]);
  }

  async loadAppointments(): Promise<void> {
    if (!this.organizationId) return;

    try {
      const response = await this.appointmentService.getOrganizationAppointments(
        this.organizationId,
        {
          status: this.filters.status as ServiceAppointmentStatus || undefined,
          page: this.currentPage,
          limit: this.pageSize
        }
      );

      this.appointments = response.appointments;
      this.totalAppointments = response.total;
      this.totalPages = response.totalPages;
    } catch (error) {
      console.error('Error loading appointments:', error);
      this.appointments = [];
      this.totalAppointments = 0;
      this.totalPages = 0;
    }
  }

  async loadStatusCounts(): Promise<void> {
    if (!this.organizationId) return;

    try {
      this.statusCounts = await this.appointmentService.getOrganizationAppointmentCounts(this.organizationId);
    } catch (error) {
      console.error('Error loading status counts:', error);
    }
  }

  // Computed stat values
  get requestedCount(): number {
    return this.statusCounts.requested;
  }

  get scheduledConfirmedCount(): number {
    return this.statusCounts.scheduled + this.statusCounts.confirmed;
  }

  get completedCount(): number {
    return this.statusCounts.completed;
  }

  // Filter methods
  applyFilters(): void {
    this.currentPage = 1;
    this.updateActiveFilterCount();
    this.loadAppointments();
  }

  updateActiveFilterCount(): void {
    let count = 0;
    if (this.filters.status) count++;
    this.activeFilterCount = count;
  }

  clearFilters(): void {
    this.filters = { status: '' };
    this.applyFilters();
  }

  // Navigation
  requestAppointment(): void {
    this.router.navigate(['/maintenance/service-appointments/new']);
  }

  viewAppointment(appointment: ServiceAppointment): void {
    this.router.navigate(['/maintenance/service-appointments', appointment.id]);
  }

  // Cancel confirmation
  openCancelModal(appointment: ServiceAppointment): void {
    this.appointmentToCancel = appointment;
    this.cancelReason = '';
    this.showCancelModal = true;
  }

  closeCancelModal(): void {
    this.appointmentToCancel = null;
    this.cancelReason = '';
    this.showCancelModal = false;
  }

  async confirmCancel(): Promise<void> {
    if (!this.appointmentToCancel || !this.cancelReason.trim()) return;

    this.isCancelling = true;
    try {
      await this.appointmentService.cancelAppointment(
        this.appointmentToCancel.id,
        this.cancelReason.trim()
      );
      this.toastService.success('Appointment cancelled successfully');
      this.closeCancelModal();
      await this.refreshList();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      this.toastService.error('Failed to cancel appointment');
    } finally {
      this.isCancelling = false;
    }
  }

  // Pagination
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadAppointments();
    }
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  // Helpers
  isCancellable(status: ServiceAppointmentStatus): boolean {
    return ['requested', 'scheduled', 'confirmed'].includes(status);
  }

  getStatusBadgeClass(status: string): string {
    const statusInfo = SERVICE_APPOINTMENT_STATUSES.find(s => s.value === status);
    if (!statusInfo) return 'bg-secondary';
    if (statusInfo.color === 'warning') return 'bg-warning text-dark';
    return `bg-${statusInfo.color}`;
  }

  getStatusText(status: string): string {
    const statusInfo = SERVICE_APPOINTMENT_STATUSES.find(s => s.value === status);
    return statusInfo?.label || status;
  }

  getPreferredTimeLabel(value: string): string {
    const option = PREFERRED_TIME_OPTIONS.find(o => o.value === value);
    return option?.label || value;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  truncateText(text: string, maxLength: number = 50): string {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }
}
